import { Component, Mixins } from 'vue-property-decorator';
import { MagicConfigMixin, convertCurrency, OmitFirst, isBreakpointDesktopQuery } from '@gig/magic-common';
import { IReward, RewardPromoType } from '@gig/magic-sdk/dist/adapter/rewards/reward';
import { LoyaltyReward } from '../loyalty-reward';
import { LoyaltyRewardModal } from '../loyalty-reward-modal';
import { magic } from '@gig/magic-sdk';
import { Currency } from '@gig/magic-sdk/dist/adapter/users/signup';
import { namespace } from 'vuex-class';
import { actions, getters, LOYALTY_NAMESPACE } from '../../../store/modules/loyalty';

const loyaltyModule = namespace(LOYALTY_NAMESPACE);

function sortRewardsByPrice(rewardA: IReward, rewardB: IReward) {
    return (
        (rewardA?.virtualCurrencyPriceToBuy || 0) -
        (rewardB?.virtualCurrencyPriceToBuy || 0)
    );
}

@Component({
    name: 'loyalty-exchange',
    components: {
        LoyaltyReward,
        LoyaltyRewardModal,
    },
})
export default class LoyaltyExchange extends Mixins(MagicConfigMixin) {
    @loyaltyModule.Action('fetchRewards')
    fetchRewards!: OmitFirst<typeof actions.fetchRewards>;

    @loyaltyModule.Action('fetchUserRewards')
    fetchUserRewards!: OmitFirst<typeof actions.fetchUserRewards>;

    @loyaltyModule.Getter('rewards')
    rewards!: ReturnType<typeof getters.rewards>;

    @loyaltyModule.Getter('userRewards')
    userRewards!: ReturnType<typeof getters.userRewards>;

    @loyaltyModule.Getter('userRewardsEchoes')
    userRewardsEchoes!: ReturnType<typeof getters.userRewardsEchoes>;

    RewardPromoType = RewardPromoType;

    isDesktop: boolean = false;

    /**
     * Boolean flag to track data fetching in progress
     */
    isLoading = false;

    /**
     * Whether to show or not the reward modal
     */
    showRewardModal: boolean = false;

    /**
     * Reward currently opened for exchange
     */
    activeReward: IReward | null = null;

    private eventUnsub: (() => void) | null = null;

    async created() {
        this.fetchData();
        this.eventUnsub = magic.events.rewardReceived(() => {
            // TODO: once merged, just re-fetch what is necessary
            this.fetchData();
        }).unsub;

        this.isDesktop = await isBreakpointDesktopQuery();
    }

    destroyed() {
        this.unsubscribe();
    }

    private async fetchData() {
        this.isLoading = true;
        const user = magic.session.getUser();
        try {
            await Promise.all([
                this.fetchRewards({ userName: user?.userName || '' }),
                this.fetchUserRewards(),
            ]);
        } catch (e) {
            console.error('Error loading data:', e);
        } finally {
            this.isLoading = false;
        }
    }

    get coinsFormatted() {
        return new Intl.NumberFormat().format(
            this.userRewardsEchoes || 0
        );
    }

    get coins(): number {
        return this.userRewardsEchoes || 0;
    }

    get sortedRewards() {
        return Object.values(this.rewards)
            .filter(
                (reward: IReward) => reward?.virtualCurrencyPriceToBuy || 0 > 0
            )
            .sort(sortRewardsByPrice);
    }

    exchangeReward(reward: IReward) {
        this.showRewardModal = true;
        this.activeReward = reward;
    }

    hideRewardModal() {
        this.showRewardModal = false;
        this.activeReward = null;
    }

    private unsubscribe() {
        if (!this.eventUnsub) {
            return;
        }

        this.eventUnsub();
        this.eventUnsub = null;
    }

    getCoinsValue(reward: IReward): number {
        return reward.virtualCurrencyPriceToBuy || 0
    }

    getCashValue(reward: IReward) {
        if (reward.promoType !== RewardPromoType.IGC_CASH && reward.promoType !== RewardPromoType.IGC_BONUS) {
            return;
        }
        return convertCurrency(reward?.euroValue || 0, this.currency);
    }

    get currency(): Currency {
        const user = magic.session.getUser();
        return user?.currency || Currency.EUR;
    }

    getSpinsNumber(reward: IReward) {
        return (reward.promoType === RewardPromoType.IGC_FREE_SPINS) ? reward.numberOfSpins : undefined;
    }

    isRewardExchangeable(reward: IReward): boolean {
        if (!reward || !reward.virtualCurrencyPriceToBuy) {
            return false;
        }
        const rewardPrice = reward?.virtualCurrencyPriceToBuy || 0;
        return (this.coins - rewardPrice >=0);
    }
}
